"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ArrowRight, Activity, Cpu, Wrench, GraduationCap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface MilestoneStep {
  step: string;
  title: string;
  badge: string;
  description: string;
  details: string;
  iconName: "activity" | "cpu" | "wrench" | "graduation";
}
const steps: MilestoneStep[] = [{
  step: "01",
  title: "Application Feasibility",
  badge: "Stage 1",
  description: "Initial assessment of sample matrices, measurement requirements, and critical quality attributes across regulated laboratory or process lines.",
  details: "Includes initial optical feasibility testing, spectroscopic baseline analysis, and risk-benefit evaluation for your exact operational environment.",
  iconName: "activity"
}, {
  step: "02",
  title: "Chemometric Method Development",
  badge: "Stage 2",
  description: "Custom calibration modeling, algorithm selection, and validation to ensure robust quantitative and qualitative analytical predictions.",
  details: "Transferable spectral libraries, outlier diagnosis protocols, and chemometric data pipelines built to strict pharmacopoeial compliance.",
  iconName: "cpu"
}, {
  step: "03",
  title: "Hardware & Process Integration",
  badge: "Stage 3",
  description: "Selection and physical mounting of high-grade analytical probes, optical interfaces, and explosion-proof flow cells directly into production units.",
  details: "Seamless connectivity with existing DCS, SCADA, and LIMS platforms with full electrical and physical enclosure qualification.",
  iconName: "wrench"
}, {
  step: "04",
  title: "On-Site Deployment & Training",
  badge: "Stage 4",
  description: "Final commissioning, IQ/OQ protocol execution, operator certification, and structured handover to ensure long-term autonomy.",
  details: "Complete documentation binder, user proficiency workshops, and ongoing SLA-backed maintenance coverage across Ireland and the UK.",
  iconName: "graduation"
}];
function StepIcon({
  iconName
}: {
  iconName: MilestoneStep["iconName"];
}) {
  if (iconName === "activity") return <Activity className="size-5 text-primary" />;
  if (iconName === "cpu") return <Cpu className="size-5 text-primary" />;
  if (iconName === "wrench") return <Wrench className="size-5 text-primary" />;
  return <GraduationCap className="size-5 text-primary" />;
}
export default function Milestones() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="processmilestones" data-nav="dark" className="bg-sidebar text-sidebar-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="page-container relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-24">
          <Badge variant="outline" className="border-sidebar-border bg-sidebar-accent text-sidebar-foreground mb-4 px-3 py-1 font-medium text-xs tracking-wider uppercase">
            Structured Delivery Model
          </Badge>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-sidebar-foreground mb-6">
            From Feasibility to Validated Deployment
          </h2>
          <p className="text-sidebar-foreground/80 text-base md:text-lg leading-relaxed">
            Our 4-step lifecycle pathway guarantees analytical precision, regulatory compliance, and seamless operational handover for regulated pharmaceutical and manufacturing facilities.
          </p>
        </div>

        {/* Pathway Container */}
        <div className="relative max-w-4xl mx-auto">
          {/* Central Vertical Connector Line (Desktop) */}
          <div aria-hidden="true" className="hidden md:block absolute left-1/2 top-8 bottom-8 w-px -translate-x-1/2 bg-sidebar-border" />

          {/* Steps List */}
          <div className="flex flex-col gap-12 md:gap-16">
            {steps.map((item, index) => {
            const isEven = index % 2 === 0;
            return <div key={item.step} data-index={index} className="relative flex flex-col md:flex-row items-center gap-8 md:gap-0">
                  {/* Left Column (Desktop) */}
                  <div className={`w-full md:w-1/2 ${isEven ? "md:pr-12 md:text-right" : "md:order-2 md:pl-12 md:text-left"}`}>
                    <motion.div initial={shouldReduceMotion ? false : {
                  opacity: 0,
                  y: 24
                }} whileInView={shouldReduceMotion ? undefined : {
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true,
                  margin: "-50px"
                }} transition={{
                  duration: 0.5,
                  delay: index * 0.1
                }} className="bg-sidebar-accent/50 border border-sidebar-border rounded-xl p-6 md:p-8 shadow-md hover:border-primary/50 transition-colors">
                      <div className={`flex items-center gap-3 mb-3 ${isEven ? "md:justify-end" : "md:justify-start"}`}>
                        <Badge variant="secondary" className="bg-primary/20 text-primary border-primary/30 font-semibold">
                          {item.badge}
                        </Badge>
                        <span className="text-xs font-mono text-sidebar-foreground/60 tracking-widest uppercase">
                          Phase {item.step}
                        </span>
                      </div>

                      <h3 className="font-default text-xl md:text-2xl font-bold text-sidebar-foreground mb-3">
                        {item.title}
                      </h3>

                      <p className="text-sidebar-foreground/80 text-sm md:text-base leading-relaxed mb-4">
                        {item.description}
                      </p>

                      <div className="pt-4 border-t border-sidebar-border/60 text-xs md:text-sm text-sidebar-foreground/70 flex items-start gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{item.details}</span>
                      </div>
                    </motion.div>
                  </div>

                  {/* Center Node Indicator */}
                  <div className="relative z-10 flex items-center justify-center shrink-0 order-first md:order-none">
                    <motion.div initial={shouldReduceMotion ? false : {
                  scale: 0.8,
                  opacity: 0
                }} whileInView={shouldReduceMotion ? undefined : {
                  scale: 1,
                  opacity: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.4,
                  delay: index * 0.1 + 0.1
                }} className="size-14 rounded-full bg-sidebar border-2 border-primary flex items-center justify-center shadow-lg shadow-primary/10">
                      <StepIcon iconName={item.iconName} />
                    </motion.div>
                  </div>

                  {/* Empty Spacer Column for Desktop Alternate Grid */}
                  <div className={`hidden md:block w-1/2 ${isEven ? "md:order-2 md:pl-12" : "md:order-1 md:pr-12"}`} aria-hidden="true" />
                </div>;
          })}
          </div>
        </div>

        {/* Bottom CTA Block */}
        <div className="mt-20 md:mt-28 text-center bg-sidebar-accent/30 border border-sidebar-border rounded-2xl p-8 md:p-12 max-w-3xl mx-auto">
          <h3 className="font-default text-2xl font-bold text-sidebar-foreground mb-3">
            Ready to evaluate your process application?
          </h3>
          <p className="text-sidebar-foreground/80 text-sm md:text-base mb-8 max-w-xl mx-auto">
            Discuss your measurement goals with our senior analytical specialists across Ireland and the UK.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                Initiate Feasibility Review
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-sidebar-border text-sidebar-foreground hover:bg-sidebar-accent">
              <Link to="/ServicesPage">
                Explore All Services
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}